import { latinizeDevanagariClinical } from './devanagari-latin.utils'
import {
  isStopTranscript,
  splitHyphenatedTens,
} from './charting-voice-transcript.utils'
import type { TxPlanStatus } from '@dataTypes/treatmentPlan.types'

export type TxPlanVoicePhaseTarget =
  | { kind: 'index'; value: number }
  | { kind: 'name'; value: string }

export type TxPlanVoiceOperation =
  | { op: 'stop' }
  | { op: 'pause' }
  | { op: 'resume' }
  | { op: 'undo' }
  | { op: 'next_phase' }
  | { op: 'previous_phase' }
  | { op: 'jump_phase'; phase: TxPlanVoicePhaseTarget }
  | { op: 'add_phase' }
  | {
      op: 'set_status'
      status: TxPlanStatus
      phase?: TxPlanVoicePhaseTarget
    }
  | {
      op: 'add'
      phase?: TxPlanVoicePhaseTarget
      teeth?: number[]
      procedureQuery: string
    }
  | { op: 'remove_last'; phase?: TxPlanVoicePhaseTarget }
  | { op: 'search'; query: string }
  | { op: 'unsupported'; reason: string }

const ONES: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
}

/** ASR often hears "tooth" as put, toot, tuth, and similar words. */
const TOOTH_WORD_ALIASES: Record<string, string> = {
  put: 'tooth',
  puts: 'tooth',
  putt: 'tooth',
  toot: 'tooth',
  toots: 'tooth',
  tuth: 'tooth',
  tut: 'tooth',
  tute: 'tooth',
  toof: 'tooth',
  toothe: 'tooth',
  tooths: 'tooth',
  truth: 'tooth',
}

/** ASR often hears "mesial" as a name. */
const SURFACE_WORD_ALIASES: Record<string, string> = {
  michel: 'mesial',
  michelle: 'mesial',
  michele: 'mesial',
  michell: 'mesial',
}

/** Command / treatment-plan mishears shown in the voice log (and used for parse fallback). */
const CLINICAL_WORD_ALIASES: Record<string, string> = {
  ...TOOTH_WORD_ALIASES,
  ...SURFACE_WORD_ALIASES,
  andu: 'undo',
  andhu: 'undo',
  ando: 'undo',
  undu: 'undo',
  ondo: 'undo',
  face: 'phase',
  boss: 'pause',
  poss: 'pause',
  pos: 'pause',
  pass: 'pause',
  carries: 'caries',
  carry: 'caries',
}

const STATUS_WORDS: Record<string, TxPlanStatus> = {
  accept: 'Accepted',
  accepted: 'Accepted',
  reject: 'Rejected',
  rejected: 'Rejected',
  complete: 'Completed',
  completed: 'Completed',
  propose: 'Proposed',
  proposed: 'Proposed',
}

const SPOKEN_DIGITS: Record<string, string> = {
  zero: '0',
  oh: '0',
  one: '1',
  two: '2',
  three: '3',
  four: '4',
  five: '5',
  six: '6',
  seven: '7',
  eight: '8',
  nine: '9',
}

/** Spoken letter names → code letter. "dito" is ASR for "D two" (seeds digit 2). */
const PROCEDURE_CODE_LETTER_ALIASES: Record<
  string,
  { letter: string; seed?: string }
> = {
  a: { letter: 'a' },
  ay: { letter: 'a' },
  b: { letter: 'b' },
  bee: { letter: 'b' },
  c: { letter: 'c' },
  see: { letter: 'c' },
  d: { letter: 'd' },
  dee: { letter: 'd' },
  dito: { letter: 'd', seed: '2' },
  e: { letter: 'e' },
  ee: { letter: 'e' },
  f: { letter: 'f' },
  ef: { letter: 'f' },
  g: { letter: 'g' },
  gee: { letter: 'g' },
  h: { letter: 'h' },
  aitch: { letter: 'h' },
  i: { letter: 'i' },
  eye: { letter: 'i' },
  j: { letter: 'j' },
  jay: { letter: 'j' },
  k: { letter: 'k' },
  kay: { letter: 'k' },
  l: { letter: 'l' },
  el: { letter: 'l' },
  m: { letter: 'm' },
  em: { letter: 'm' },
  n: { letter: 'n' },
  en: { letter: 'n' },
  o: { letter: 'o' },
  oh: { letter: 'o' },
  p: { letter: 'p' },
  pee: { letter: 'p' },
  q: { letter: 'q' },
  cue: { letter: 'q' },
  r: { letter: 'r' },
  are: { letter: 'r' },
  s: { letter: 's' },
  ess: { letter: 's' },
  t: { letter: 't' },
  tee: { letter: 't' },
  u: { letter: 'u' },
  you: { letter: 'u' },
  v: { letter: 'v' },
  vee: { letter: 'v' },
  w: { letter: 'w' },
  doubleyou: { letter: 'w' },
  x: { letter: 'x' },
  ex: { letter: 'x' },
  y: { letter: 'y' },
  why: { letter: 'y' },
  z: { letter: 'z' },
  zee: { letter: 'z' },
  zed: { letter: 'z' },
}

/**
 * Lead token for a spoken procedure code: letter/"dee", fused "d2"…"z9", or ASR "dito".
 * Returns seeded first digit when ASR glued the letter and first digit together.
 */
const parseProcedureCodeLeadToken = (
  token: string,
): { letter: string; initialDigits: string[] } | null => {
  const normalized = token.toLowerCase()
  const alias = PROCEDURE_CODE_LETTER_ALIASES[normalized]
  if (alias) {
    return {
      letter: alias.letter,
      initialDigits: alias.seed ? [alias.seed] : [],
    }
  }
  const fused = /^([a-z])([0-9])$/.exec(normalized)
  if (fused?.[1] && fused[2]) {
    return { letter: fused[1], initialDigits: [fused[2]] }
  }
  return null
}

/** “D two three nine two”, “b1 two three four”, and ASR “dito three nine two” → d2392 / b1234. */
const collapseSpokenProcedureCode = (text: string): string => {
  const tokens = text.split(' ').filter(Boolean)
  const next: string[] = []
  for (let index = 0; index < tokens.length; ) {
    const token = tokens[index]
    const lead = parseProcedureCodeLeadToken(token)
    if (lead) {
      const digits: string[] = [...lead.initialDigits]
      let cursor = index + 1
      while (digits.length < 4 && cursor < tokens.length) {
        // "oh" is a spoken zero here, not letter O (lead already consumed).
        const digit = SPOKEN_DIGITS[tokens[cursor].toLowerCase()] ?? ''
        const numeric = /^\d$/.test(tokens[cursor]) ? tokens[cursor] : ''
        const nextDigit = digit || numeric
        if (!nextDigit) break
        digits.push(nextDigit)
        cursor += 1
      }
      if (digits.length === 4) {
        next.push(`${lead.letter}${digits.join('')}`)
        index = cursor
        continue
      }
    }
    next.push(token)
    index += 1
  }
  return next.join(' ')
}

/** Show tooth/phase numbers as digits after CDT codes are collapsed. */
const digitizeTxPlanTokens = (text: string): string => {
  const tokens = text.split(' ').filter(Boolean)
  const next: string[] = []
  for (let index = 0; index < tokens.length; ) {
    const token = tokens[index].toLowerCase()
    const following = tokens[index + 1]?.toLowerCase()
    if (token === 'twenty' || token === 'thirty') {
      const base = token === 'twenty' ? 20 : 30
      const ones =
        following && following in ONES && ONES[following] < 10
          ? ONES[following]
          : undefined
      if (ones !== undefined && ones > 0 && base + ones <= 32) {
        next.push(String(base + ones))
        index += 2
        continue
      }
      next.push(String(base))
      index += 1
      continue
    }
    if (token in ONES) {
      next.push(String(ONES[token]))
      index += 1
      continue
    }
    if (SPOKEN_DIGITS[token] !== undefined) {
      next.push(SPOKEN_DIGITS[token])
      index += 1
      continue
    }
    next.push(tokens[index])
    index += 1
  }
  return next.join(' ')
}

const isToothNumberToken = (token: string | undefined): boolean => {
  if (!token) return false
  if (token in ONES || token === 'twenty' || token === 'thirty') return true
  if (!/^\d+$/.test(token)) return false
  const value = Number(token)
  return value >= 1 && value <= 32
}

const matchTokenCase = (source: string, replacement: string): string => {
  if (!source) return replacement
  if (source.length > 1 && source === source.toUpperCase()) {
    return replacement.toUpperCase()
  }
  const first = source.charAt(0)
  if (first && first === first.toUpperCase()) {
    return replacement.charAt(0).toUpperCase() + replacement.slice(1)
  }
  return replacement
}

/** Rewrite clinical mishears for display or parse; optionally keep ASR casing. */
const rewriteClinicalWords = (text: string, preserveCase: boolean): string => {
  const tokens = text.split(' ').filter(Boolean)
  const next: string[] = []
  for (let index = 0; index < tokens.length; ) {
    const token = tokens[index] ?? ''
    const lower = token.toLowerCase()
    const following = (tokens[index + 1] ?? '').toLowerCase()
    const afterFollowing = (tokens[index + 2] ?? '').toLowerCase()
    if (
      lower === 'to' &&
      following === 'the' &&
      isToothNumberToken(afterFollowing)
    ) {
      next.push(preserveCase ? matchTokenCase(token, 'tooth') : 'tooth')
      index += 2
      continue
    }
    if (lower === 'and' && following === 'do') {
      next.push(preserveCase ? matchTokenCase(token, 'undo') : 'undo')
      index += 2
      continue
    }
    const alias = CLINICAL_WORD_ALIASES[lower]
    if (alias) {
      next.push(preserveCase ? matchTokenCase(token, alias) : alias)
      index += 1
      continue
    }
    next.push(preserveCase ? token : lower)
    index += 1
  }
  return next.join(' ')
}

export const formatTxPlanTranscript = (text: string): string =>
  rewriteClinicalWords(
    digitizeTxPlanTokens(
      collapseSpokenProcedureCode(
        splitHyphenatedTens(latinizeDevanagariClinical(text))
          .replace(/[.]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim(),
      ),
    ),
    true,
  )

/** Fallback for backends that do not emit `normalized` yet. */
const normalizeLegacyTranscript = (text: string): string =>
  rewriteClinicalWords(
    collapseSpokenProcedureCode(
      formatTxPlanTranscript(text)
        .toLowerCase()
        .replace(/\b([a-z])\s+(\d{3,5})\b/g, '$1$2')
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim(),
    ),
    false,
  )

const readNumber = (
  tokens: string[],
  index: number,
): { value: number; next: number } | null => {
  const token = tokens[index]
  if (!token) return null
  if (/^\d+$/.test(token)) {
    const value = Number(token)
    if (value >= 1 && value <= 32) return { value, next: index + 1 }
    return null
  }
  if (token in ONES) return { value: ONES[token], next: index + 1 }
  if (token === 'twenty' || token === 'thirty') {
    const ones = tokens[index + 1]
    const base = token === 'twenty' ? 20 : 30
    if (ones && ones in ONES && ONES[ones] < 10) {
      const value = base + ONES[ones]
      if (value <= 32) return { value, next: index + 2 }
    }
    if (base <= 32) return { value: base, next: index + 1 }
  }
  return null
}

const readPhaseTokens = (
  tokens: string[],
  index: number,
): { phase: TxPlanVoicePhaseTarget; next: number } | null => {
  const numeric = readNumber(tokens, index)
  if (numeric) {
    return {
      phase: { kind: 'index', value: numeric.value },
      next: numeric.next,
    }
  }
  const name = tokens[index]
  if (!name || name === 'tooth' || name === 'teeth' || name === 'code') {
    return null
  }
  return { phase: { kind: 'name', value: name }, next: index + 1 }
}

const readToothList = (
  tokens: string[],
  index: number,
): { teeth: number[]; next: number } | null => {
  const teeth: number[] = []
  let cursor = index
  while (cursor < tokens.length) {
    if (tokens[cursor] === 'and') {
      cursor += 1
      continue
    }
    const numeric = readNumber(tokens, cursor)
    if (!numeric) break
    teeth.push(numeric.value)
    cursor = numeric.next
  }
  if (teeth.length === 0) return null
  return { teeth, next: cursor }
}

const parsePhaseTarget = (text: string): TxPlanVoicePhaseTarget | null => {
  const tokens = text.trim().split(/\s+/).filter(Boolean)
  const parsed = readPhaseTokens(tokens, 0)
  if (!parsed || parsed.next !== tokens.length) return null
  return parsed.phase
}

const cleanSearchQuery = (raw: string): string =>
  raw
    .replace(/\b(?:procedures?|codes?|the|a|an|for)\b/g, ' ')
    .replace(/\bcarries\b/g, 'caries')
    .replace(/\bcarry\b/g, 'caries')
    .replace(/\s+/g, ' ')
    .trim()

const unsupported = (reason: string): TxPlanVoiceOperation => ({
  op: 'unsupported',
  reason,
})

const parseAdd = (text: string): TxPlanVoiceOperation => {
  const tokens = text.split(/\s+/).filter(Boolean)
  let phase: TxPlanVoicePhaseTarget | undefined
  const teeth: number[] = []
  const kept: string[] = []
  for (let index = 0; index < tokens.length; ) {
    const token = tokens[index]
    if (token === 'phase') {
      const parsed = readPhaseTokens(tokens, index + 1)
      if (!parsed) return unsupported('Say a phase number.')
      if (phase) return unsupported('Say one phase.')
      phase = parsed.phase
      index = parsed.next
      continue
    }
    if (
      token === 'tooth' ||
      token === 'teeth' ||
      token === 'number' ||
      token === 'numbers' ||
      token === 'num'
    ) {
      let cursor = index + 1
      while (
        tokens[cursor] === 'tooth' ||
        tokens[cursor] === 'teeth' ||
        tokens[cursor] === 'number' ||
        tokens[cursor] === 'numbers' ||
        tokens[cursor] === 'num'
      ) {
        cursor += 1
      }
      const parsed = readToothList(tokens, cursor)
      if (!parsed) return unsupported('Say a tooth number from 1 to 32.')
      teeth.push(...parsed.teeth)
      index = parsed.next
      continue
    }
    if (token === 'and' && teeth.length > 0 && readNumber(tokens, index + 1)) {
      index += 1
      continue
    }
    const spokenTooth = readNumber(tokens, index)
    if (spokenTooth) {
      teeth.push(spokenTooth.value)
      index = spokenTooth.next
      continue
    }
    if (token === 'code') {
      index += 1
      continue
    }
    kept.push(token)
    index += 1
  }
  const procedureQuery = kept.join(' ').trim()
  if (!procedureQuery && phase && teeth.length === 0) {
    return { op: 'jump_phase', phase }
  }
  if (!procedureQuery && teeth.length > 0) {
    return {
      op: 'add',
      ...(phase ? { phase } : {}),
      teeth,
      procedureQuery,
    }
  }
  if (!procedureQuery) return unsupported('Say a procedure name or code.')
  return {
    op: 'add',
    ...(phase ? { phase } : {}),
    ...(teeth.length > 0 ? { teeth } : {}),
    procedureQuery,
  }
}

const tidyNormalizedTranscript = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const normalizeBackendTranscript = (text: string): string =>
  collapseSpokenProcedureCode(tidyNormalizedTranscript(text))

const parseNormalizedTxPlanTranscript = (
  text: string,
): TxPlanVoiceOperation[] => {
  if (!text) return [unsupported('Say a treatment plan command.')]
  if (isStopTranscript(text)) return [{ op: 'stop' }]
  if (/^(?:pause|boss|poss|pos|pass)$/.test(text)) return [{ op: 'pause' }]
  if (text === 'resume' || text === 'continue') return [{ op: 'resume' }]
  if (text === 'undo') return [{ op: 'undo' }]
  if (/^(?:add|new) phase$/.test(text)) return [{ op: 'add_phase' }]
  if (
    /^(?:remove|clear) last(?: procedure)?$/.test(text) ||
    text === 'undo last' ||
    text === 'undo last procedure'
  ) {
    return [{ op: 'remove_last' }]
  }
  if (
    /^(?:next|previous)(?: phase)?$/.test(text) ||
    text === 'back' ||
    text === 'back phase'
  ) {
    return [{ op: text.startsWith('next') ? 'next_phase' : 'previous_phase' }]
  }
  const jump = text.match(/^(?:go to|jump to|select) phase (.+)$/)
  if (jump) {
    const phase = parsePhaseTarget(jump[1])
    return phase
      ? [{ op: 'jump_phase', phase }]
      : [unsupported('Say a phase number.')]
  }
  const status = text.match(
    /^(accept|accepted|reject|rejected|complete|completed|propose|proposed)(?: (.+))?$/,
  )
  if (status) {
    const mapped = STATUS_WORDS[status[1]]
    if (!status[2]) return [{ op: 'set_status', status: mapped }]
    const phaseWords = status[2].match(/^phase (.+)$/)
    if (!phaseWords) return [unsupported('Say the phase to update.')]
    const phase = parsePhaseTarget(phaseWords[1])
    return phase
      ? [{ op: 'set_status', status: mapped, phase }]
      : [unsupported('Say a phase number.')]
  }
  const search = text.match(/^(?:search(?: for)?|find|look up) (.+)$/)
  if (search) {
    const query = cleanSearchQuery(search[1])
    return query
      ? [{ op: 'search', query }]
      : [unsupported('Say what to search for.')]
  }
  return [parseAdd(text)]
}

export const parseTxPlanTranscript = (raw: string): TxPlanVoiceOperation[] =>
  parseNormalizedTxPlanTranscript(normalizeLegacyTranscript(raw))

export type TxPlanFinalTranscript = {
  text: string
  formatted?: string
  normalized?: string
}

/** Backend `normalized` is canonical; otherwise the local mishear rewrite runs on `formatted ?? text`. */
export const parseTxPlanFinalTranscript = ({
  text,
  formatted,
  normalized,
}: TxPlanFinalTranscript): TxPlanVoiceOperation[] =>
  typeof normalized === 'string'
    ? parseNormalizedTxPlanTranscript(normalizeBackendTranscript(normalized))
    : parseTxPlanTranscript(formatted ?? text)
